<template>
  <div class="modern-product-card animate-fade-in">
    <!-- Best seller pill -->
    <div class="best-seller-badge" v-if="product.best_seller">
      <v-icon icon="mdi-fire" size="14" class="mr-1" />
      <span>Best Seller</span>
    </div>

    <!-- Product Image Wrapper -->
    <div class="image-wrapper bg-grey-lighten-4" @click="Configure">
      <v-img
        :lazy-src="ProductImage(product)"
        class="product-image"
        :src="ProductImage(product)"
      />
    </div>

    <!-- Content Area -->
    <div class="card-content">
      <!-- Title -->
      <div class="title-wrap">
        <a :href="ConfigSinglePageUrl+'/'+product.slug" class="product-title-link clip-text-1-lines">
          {{ product.name }}
        </a>
      </div>

      <!-- Description / Excerpt -->
      <div class="excerpt-wrap">
        <p class="product-excerpt clip-text-2-lines">
          {{ product.description.replace(/<[^>]*>?/gm, '') }}
        </p>
      </div>

      <!-- Info Footer (Price & Badges) -->
      <div class="card-meta">
        <!-- Price -->
        <div class="price-section" v-if="ConfigShowPricing">
          <span class="starts-at-label">Starts at</span>
          <span class="price-value">${{ product.price }}</span>
        </div>

        <!-- Free Delivery Badge -->
        <div v-if="free_shipping" class="free-delivery-badge">
          <v-icon icon="mdi-truck-delivery" size="14" class="mr-1" />
          <span>Free Delivery</span>
        </div>
      </div>
    </div>

    <!-- Action Footer -->
    <div class="card-action-footer">
      <v-btn
        size="large"
        rounded="lg"
        variant="flat"
        block
        @click="Configure"
        class="cta-btn font-weight-medium"
        :color="ConfigElementsColor"
        prepend-icon="mdi-file-document-edit-outline"
      >
        Get a Quote
      </v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useGlobalsStore } from '@/stores/globals'

const props = defineProps<{
  product: any
}>()

const store = useGlobalsStore()
const {
  free_shipping,
  is_fetching,
  is_mobile,
  datasets,
  product_reviews,
  ConfigSinglePageUrl,
  ConfigShowPricing,
  ConfigElementsColor
} = storeToRefs(store)

function Configure() {
  store.SetSelectedCopier(props.product)
  store.SetState('configurator', true)
}

const Accessories = computed(() => {
  return datasets.value.filter((ds: any) => ds.product_id == props.product.id)
})

const ProductReviews = computed(() => {
  return product_reviews.value.filter((pr: any) => pr.product_id === props.product.id)
})

const ProductImage = store.ProductImage
</script>

<style scoped lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&family=Inter:wght@400;500;600&display=swap');

.modern-product-card {
  font-family: 'Inter', sans-serif;
  position: relative;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0,0,0,0.02);

  &:hover {
    transform: translateY(-4px);
    border-color: #d1d5db;
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);

    .product-image {
      transform: scale(1.06);
    }
  }
}

/* Glassmorphic Best Seller Badge */
.best-seller-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 10;
  display: flex;
  align-items: center;
  background: rgba(32, 158, 97, 0.9);
  backdrop-filter: blur(4px);
  color: #ffffff;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  box-shadow: 0 4px 10px rgba(32, 158, 97, 0.2);
}

/* Image Container */
.image-wrapper {
  position: relative;
  height: 200px;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #f3f4f6;
  padding: 0px;

  .product-image {
    width: 100%;
    height: 100%;
    transition: transform 0.4s ease;
  }
}

/* Content Area */
.card-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.title-wrap {
  margin-bottom: 6px;
}

.product-title-link {
  font-family: 'Outfit', sans-serif;
  text-decoration: none;
  cursor: pointer;
  color: #111827;
  font-weight: 600;
  font-size: 17px;
  transition: color 0.2s ease;

  &:hover {
    color: #1d4ed8;
  }
}

.excerpt-wrap {
  margin-bottom: 16px;
  flex-grow: 1;
}

.product-excerpt {
  font-size: 13px;
  line-height: 1.5;
  color: #4b5563;
  margin: 0;
}

/* Meta Info */
.card-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: auto;
  border-top: 1px dashed #e5e7eb;
  padding-top: 12px;
}

.price-section {
  display: flex;
  flex-direction: column;

  .starts-at-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #9ca3af;
    font-weight: 500;
  }

  .price-value {
    font-family: 'Outfit', sans-serif;
    font-size: 22px;
    font-weight: 700;
    color: #111827;
    line-height: 1.1;
  }
}

.free-delivery-badge {
  display: inline-flex;
  align-items: center;
  color: #059669;
  font-size: 12px;
  font-weight: 600;
  background: #ecfdf5;
  padding: 4px 8px;
  border-radius: 6px;
  width: fit-content;
}

/* Action Footer */
.card-action-footer {
  padding: 0 16px 16px 16px;

  .cta-btn {
    text-transform: none;
    letter-spacing: 0;
    font-size: 14px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;

    &:hover {
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
      transform: translateY(-1px);
    }
  }
}

/* Dark Mode support */
.dark-theme .modern-product-card,
.dark .modern-product-card {
  background: #1e1e2d;
  border-color: #2d2d3f;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);

  &:hover {
    border-color: #3b3b55;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
  }

  .image-wrapper {
    background: #1b1b26 !important;
    border-bottom-color: #2d2d3f;
  }

  .product-title-link {
    color: #f3f4f6;

    &:hover {
      color: #60a5fa;
    }
  }

  .product-excerpt {
    color: #9ca3af;
  }

  .card-meta {
    border-top-color: #2d2d3f;
  }

  .price-section {
    .price-value {
      color: #ffffff;
    }
  }

  .free-delivery-badge {
    background: rgba(5, 150, 105, 0.15);
    color: #34d399;
  }
}
</style>
